export type TextTable = (number | string)[][]

/**
 * テーブルデータをクリップボードにコピー可能な文字列に変換します。
 *
 * @param table - テーブルデータ。
 * @returns クリップボードにコピー可能な文字列。
 */
export const convertTableToClipboardData = (table: TextTable): string =>
  table
    .map((row) =>
      row
        .map((value) => (`${value}`.includes('\n') ? `"${value}"` : `${value}`))
        .join('\t'),
    )
    .join('\n')

const vTable = (size = 1): TextTable =>
  Array(size)
    .fill(undefined)
    .map(() => [])

const hTable = (size = 1): TextTable => [Array(size).fill('')]

export const normalizeTable = (table: TextTable) => {
  const columnSize = Math.max(...table.map((lowRow) => lowRow.length))
  if (!Number.isFinite(columnSize)) {
    return table
  }

  const emptyRow = Array(columnSize).fill('')

  return table.map((row) => [...row, ...emptyRow].slice(0, columnSize))
}

export const stackVTables = (...tables: TextTable[]) =>
  tables.length > 0
    ? tables.reduce((table1, table2) => [...table1, ...table2])
    : []

export const stackVTablesWithOperator = (
  operator: 'AND' | 'OR',
  ...tables: TextTable[]
) =>
  tables.length > 0
    ? tables.reduce((table1, table2) => [...table1, [operator], ...table2])
    : []

export const stackHTables = (...tables: TextTable[]): TextTable =>
  tables.length > 0
    ? tables.reduce((table1, table2) => {
        const table1RowSize = table1.length
        const table2RowSize = table2.length

        const normalizedTable1 = normalizeTable(
          table1RowSize < table2RowSize
            ? stackVTables(table1, vTable(table2RowSize - table1RowSize))
            : table1,
        )
        const normalizedTable2 = normalizeTable(
          table1RowSize < table2RowSize
            ? table2
            : stackVTables(table2, vTable(table1RowSize - table2RowSize)),
        )

        return normalizedTable1.map((table1Row, index) => [
          ...table1Row,
          ...normalizedTable2[index],
        ])
      })
    : []

export const shiftVTable = (table: TextTable, size = 1) =>
  stackVTables(vTable(size), table)

export const shiftHTable = (table: TextTable, size = 1) =>
  stackHTables(hTable(size), table)
